import { BasePoint, RegionMapResponse, RegionResponse } from '@/apis/v2Api'
import { createStore } from 'vuex'
import { Device, RelocationZone } from '@/types/bike'
import { getBasePoint } from '@/apis/serviceApi'
import { hasRole } from '@/server/helper/authHelper'
import { LeftType, Title } from '@/enums/header'
import { Region } from '@/types/mapTypes'
import { User, UserArea } from '@/types/authTypes'
import * as authApi from '@/apis/authApi'
import * as v2Api from '@/apis/v2Api'
import Role from '@/enums/Role'

export const mappingIfSameId = (
  source: any[],
  sourceId: string,
  target: any,
  targetId: string,
  targetAttrName: string
) => {
  if (source.length > 0) {
    for (const item of target) {
      let cursor = 0

      while (cursor < source.length && item[targetId] !== source[cursor][sourceId]) {
        ++cursor
      }

      if (cursor === source.length) {
        break
      }

      item[targetAttrName] = source[cursor]
    }
  }
}

export enum MutationName {
  ALL_BASE_POINT_MAP = 'allBasePointMap',
  ALL_BASE_POINTS = 'allBasePoints',
  BASE_POINT_MAP = 'basePointMap',
  BASE_POINTS = 'basePoints',
  CURRENT_BASE_POINT = 'currentBasePoint',
  CURRENT_REGION = 'currentRegion',
  DEFAULT_AREA = 'defaultArea',
  DEVICE_CODE_MAP = 'deviceCodeMap',
  DEVICE_FILTER = 'deviceFilter',
  DEVICE_PARTS = 'deviceParts',
  DEVICES = 'devices',
  RELOCATION_ZONES = 'relocationZones',
  IS_DARK_TOAST = 'isDarkToast',
  IS_SET_DEVICES = 'isSetDevices',
  LOADING = 'loading',
  ME = 'me',
  MY_REGIONS = 'myRegions',
  REGION_MAP = 'regionMap',
  REGIONS = 'regions',
  SEARCHED_DEVICE_CODE = 'searchedDeviceCode',
  SET_HEADER = 'setHeader',
  SET_SHOW_BOTTOM_NAVIGATION = 'setShowBottomNavigation',
  SET_TOAST = 'setToast',
}

export enum Action {
  LOGIN = 'login',
  GET_ME = 'getMe',
  UPDATE_BASE_POINTS = 'updateBasePoints',
  UPDATE_MY_REGIONS = 'updateMyRegions',
  UPDATE_CURRENT_BASE_POINT = 'updateCurrentBasePoint',
  UPDATE_CURRENT_REGION = 'updateLastRegion',
  GET_DEVICE_PARTS = 'getDeviceParts',
  UPDATE_LOADING = 'loading',
}

export interface ToastState {
  message?: string
  show: boolean
}

export interface HeaderState {
  show: boolean
  title: Title
  leftType: LeftType
}

export type BasePointMap = Record<number, BasePoint>
export type RegionMap = Record<number, RegionMapResponse>
export type DeviceCodeMap = Record<string, Device>
export type RelocationZoneMap = Record<number, RelocationZone>

export interface State {
  loading: false
  me: User
  toast: ToastState
  allBasePoints: BasePoint[]
  allBasePointMap: BasePointMap
  basePoints: BasePoint[]
  basePointMap: BasePointMap
  defaultArea: any // FIXME: 타입 확인 후 수정
  currentRegion: Region
  currentBasePoint: BasePoint
  regions: Array<Region>
  myRegions: Array<Region>
  isSetDevices: boolean
  devices: Device[]
  relocationZones: RelocationZone[]
  deviceCodeMap: DeviceCodeMap
  relocationZoneMap: RelocationZoneMap
  deviceFilter: number
  regionMap: RegionMap
  header: HeaderState
  deviceParts: any[]
  searchedDeviceCode: string
  isDarkToast: boolean
}

const store = createStore<State>({
  state: {
    // FIXME: interface 에 맞는 초기값 설정 필요
    loading: false,
    me: {} as User,
    allBasePoints: [] as BasePoint[],
    allBasePointMap: {} as BasePointMap,
    basePoints: [] as BasePoint[],
    basePointMap: {} as BasePointMap,
    defaultArea: {} as any,
    currentRegion: {} as Region,
    currentBasePoint: {} as BasePoint,
    regions: [] as Array<Region>,
    myRegions: [] as Array<Region>,
    toast: { message: '잠시 후 다시 시도해주세요.', show: false },
    isSetDevices: false,
    devices: [] as Device[],
    relocationZones: [] as RelocationZone[],
    deviceCodeMap: {} as DeviceCodeMap,
    relocationZoneMap: {} as RelocationZoneMap,
    deviceFilter: {} as number,
    regionMap: {} as RegionMap,
    header: {
      show: false,
      title: Title.DEFAULT,
      leftType: LeftType.DEFAULT,
    },
    deviceParts: [],
    searchedDeviceCode: '',
    isDarkToast: false,
  },
  mutations: {
    [MutationName.LOADING](state, value) {
      if (state[MutationName.LOADING] === value) {
        return
      }
      state[MutationName.LOADING] = value
    },
    [MutationName.ME](state, value) {
      state.me = value
    },
    [MutationName.REGIONS](state, value) {
      state[MutationName.REGIONS] = value
    },
    [MutationName.REGION_MAP](state, value) {
      state[MutationName.REGION_MAP] = value
    },
    [MutationName.BASE_POINTS](state, value) {
      state.basePoints = value
    },
    [MutationName.BASE_POINT_MAP](state, value) {
      state[MutationName.BASE_POINT_MAP] = value
    },
    [MutationName.DEFAULT_AREA](state, value) {
      state.defaultArea = value
    },
    [MutationName.CURRENT_BASE_POINT](state, value) {
      state.currentBasePoint = value
    },
    [MutationName.CURRENT_REGION](state, value) {
      state.currentRegion = value
    },
    [MutationName.REGIONS](state, value) {
      state.regions = value
    },
    [MutationName.MY_REGIONS](state, value: Array<Region>) {
      state.regions = value
    },
    [MutationName.SET_TOAST](state, value: ToastState) {
      state.toast = {
        ...value,
      }
    },
    [MutationName.IS_SET_DEVICES](state, value) {
      state.isSetDevices = value
    },
    [MutationName.DEVICES](state, value) {
      state.devices = value
      state.deviceCodeMap = value.reduce(
        (p: object, device: Device) =>
          Object.assign(p, {
            [device.code]: device,
          }),
        {}
      ) as DeviceCodeMap
    },
    [MutationName.RELOCATION_ZONES](state, value) {
      state.relocationZoneMap = value
    },
    [MutationName.DEVICE_FILTER](state, value) {
      state.deviceFilter = value
    },
    [MutationName.SET_HEADER](state, value: HeaderState) {
      state.header = {
        ...value,
      }
    },
    [MutationName.DEVICE_PARTS](state, value) {
      state.deviceParts = value
    },
    [MutationName.ALL_BASE_POINTS](state, value) {
      state.allBasePoints = value
    },
    [MutationName.ALL_BASE_POINT_MAP](state, value) {
      state.allBasePointMap = value
    },
    [MutationName.SEARCHED_DEVICE_CODE](state, value) {
      state.searchedDeviceCode = value
    },
    [MutationName.IS_DARK_TOAST](state, value) {
      state.isDarkToast = value
    },
  },
  actions: {
    async [Action.LOGIN]({ commit }, { username, password }): Promise<boolean> {
      try {
        await authApi.login({ username, password })
        return true
      } catch (e: any) {
        return false
      }
    },
    async [Action.GET_ME]({ commit }): Promise<boolean> {
      try {
        const me = await v2Api.getMe()
        commit(MutationName.ME, me)

        if (me.id == null) {
          return false
        }

        if (hasRole({ role: Role.INSURANCE, operator: me })) {
          await authApi.logout()
          location.href = '/'
        }

        const [regions, basePoints] = await Promise.all<RegionResponse[], BasePoint[]>([
          v2Api.getRegions(),
          getBasePoint(),
        ])

        mappingIfSameId(regions, 'id', me.areas, 'region_id', 'region')

        const defaultArea = me.areas.find((area: UserArea) => area.is_show)
        const currentRegion = regions.find((region: any) => defaultArea.region_id === region.id)

        const myBasePoints = await v2Api.getMyBasePoints({
          user_id: store.state[MutationName.ME].id,
          region_id: defaultArea.region_id ?? 0,
        })

        const basePointId = defaultArea.base_point_id
        const basePoint = myBasePoints.find((basePoint) => basePoint.id === basePointId) || myBasePoints[0]

        const basePointMap = {} as BasePointMap
        basePoints.forEach((basePoint: BasePoint) => {
          basePointMap[basePoint.id] = basePoint
        })

        const regionMap = {} as RegionMap
        regions.forEach((region: RegionResponse) => {
          regionMap[region.id] = region
        })

        commit(MutationName.CURRENT_BASE_POINT, basePoint)
        commit(MutationName.BASE_POINTS, myBasePoints)
        commit(MutationName.REGIONS, regions)
        commit(MutationName.REGION_MAP, regionMap)
        commit(MutationName.DEFAULT_AREA, defaultArea)
        commit(MutationName.BASE_POINT_MAP, basePointMap)
        commit(MutationName.ALL_BASE_POINTS, basePoints)
        commit(MutationName.ALL_BASE_POINT_MAP, basePointMap)
        commit(MutationName.CURRENT_REGION, currentRegion || defaultArea.region)
        return true
      } catch (e: any) {
        console.debug(e)
        return false
      }
    },
    async [Action.GET_DEVICE_PARTS](store): Promise<any> {
      try {
        const deviceParts = await v2Api.getDeviceParts()
        const marshalled = deviceParts.flatMap((devicePart: any) =>
          devicePart.groups.map((group: any) => ({
            name: group.name,
            items: group.parts.map((part: any) => ({
              id: part.id,
              name: part.name,
            })),
          }))
        )
        store.commit(MutationName.DEVICE_PARTS, marshalled)
      } catch (e: any) {
        console.error(e)
      }
    },
    async [Action.UPDATE_CURRENT_REGION](store, regionId: number) {
      const currentBaseRegion = store.state[MutationName.REGIONS].find((region: Region) => regionId === region.id)

      await v2Api.changeMyDefaultRegion({
        user_id: store.state[MutationName.ME].id,
        region_id: regionId,
      })

      store.commit(MutationName.CURRENT_REGION, currentBaseRegion)

      if (currentBaseRegion) {
        const newDefaultArea = store.state.me.areas.find((area) => area.region_id === currentBaseRegion.id)
        store.commit(MutationName.DEFAULT_AREA, newDefaultArea)
      }
    },
    async [Action.UPDATE_CURRENT_BASE_POINT](store, basePointId: number) {
      await v2Api.changeMyDefaultBasePoints({
        user_id: store.state[MutationName.ME].id,
        region_id: store.state[MutationName.CURRENT_REGION].id,
        base_point_id: basePointId,
      })

      const currentBasePoint = store.state[MutationName.BASE_POINTS].find(
        (basePoint: BasePoint) => basePointId === basePoint.id
      )

      store.commit(MutationName.CURRENT_BASE_POINT, currentBasePoint)
    },
    async [Action.UPDATE_BASE_POINTS](store, currentRegionId: number) {
      store.state[MutationName.BASE_POINTS].splice(0, store.state[MutationName.BASE_POINTS].length)
      const myBasePoints = await v2Api.getMyBasePoints({
        user_id: store.state[MutationName.ME].id,
        region_id: currentRegionId,
      })
      store.commit(MutationName.BASE_POINTS, myBasePoints)

      const basePointId = store.state[MutationName.DEFAULT_AREA].base_point_id
      const basePoint = myBasePoints.find((basePoint) => basePoint.id === basePointId) || myBasePoints[0]

      if (basePoint) {
        store.commit(MutationName.CURRENT_BASE_POINT, basePoint)
      }
    },
  },
  getters: {},
  modules: {},
})

export default store
